import { useEffect, useMemo, useState } from 'react';
import { Card } from '@dycs/ui';
import {
  QUOTE_TENANTS,
  TICKET_TYPE_LABELS,
  normalizeSites,
  type QuoteKind,
  type TicketType,
  type Tenant,
} from '@dycs/shared';
import { loadTenant, readEmbedParams, readFollowUpKey, type ChatTurn } from '@/lib/firebase';
import { FollowUpPanel } from '@/modules/FollowUpPanel';
import { IntakeForm } from '@/modules/IntakeForm';
import { ChatbotPanel } from '@/modules/ChatbotPanel';
import { PraiseForm } from '@/modules/PraiseForm';
import { QuoteForm } from '@/modules/QuoteForm';

/** 'praise' = 칭찬 릴레이 창구(tenants.praiseRelay) — 티켓 유형은 문의지만 폼이 따로다. */
/** 'quote' = 전세버스 견적(QUOTE_TENANTS 창구) — tickets 가 아니라 quotes 로 간다. */
type View = 'select' | 'inquiry' | 'lost_item' | 'praise' | 'quote';

/**
 * 유형 진입 시 챗봇을 건너뛰고 접수 폼을 바로 열지 — 선택 클릭과 ?type= 고정 링크가 같은 분기를 쓴다.
 *   분실물은 FAQ 대상이 아니고, 챗봇 OFF 거래처·?intake=1 진입도 폼 직행.
 */
function skipChatbot(t: TicketType, tenant: Tenant, direct: boolean): boolean {
  return direct || t === 'lost_item' || !tenant.faqEnabled;
}
type LoadState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ok'; tenant: Tenant };

export function App() {
  const [load, setLoad] = useState<LoadState>({ status: 'loading' });
  const [view, setView] = useState<View>('select');
  // 챗봇으로 해결 안 돼 접수 폼으로 전환됐는지(접수 source='chatbot' 태깅).
  const [fromChatbot, setFromChatbot] = useState(false);
  const [showIntake, setShowIntake] = useState(false);
  // ?intake=1 로 들어왔는지 — 유형 선택 후 챗봇을 건너뛰고 바로 접수 폼으로 간다.
  const [directIntake, setDirectIntake] = useState(false);
  // ?type=inquiry|lost_item — 유형이 고정된 링크. 선택 화면·뒤로가기를 없애고 그 창구만 연다.
  const [entryType, setEntryType] = useState<TicketType | null>(null);
  // 챗봇 대화 — 상담원 접수로 전환되면 티켓 스레드에 그대로 옮겨 담는다.
  const [chatHistory, setChatHistory] = useState<ChatTurn[]>([]);
  /**
   * 「이어서 문의하기」 링크(`?f=`)로 들어왔나 (2026-09-09).
   * 🔴 기존 진입(`?tenant=`·`?type=`·`?intake=`)은 **한 글자도 안 바뀐다** — buslink 승객앱이
   *    이 위젯을 iframe 으로 물고 있어(외부 소비자) 파라미터 계약을 건드리면 조용히 죽는다.
   *    `?f=` 는 **더하기만** 했고, 없으면 예전 화면 그대로다.
   */
  const [followUpKey, setFollowUpKey] = useState<string | null>(null);
  /**
   * 고객이 문의 폼에서 「분실물 접수로 옮기기」를 눌렀나 (2026-09-10).
   * 🔴 이 경우 **화면 제목이 `?type=` 링크가 아니라 지금 쓰는 유형을 따라야 한다** — 안 그러면
   *    «고객 문의» 제목 아래 분실물 폼이 떠서 고객이 잘못 눌렀다고 여긴다. 안 누른 사람에게는
   *    한 글자도 안 바뀐다(기존 링크 계약 불변 — buslink 승객앱이 iframe 으로 물고 있다).
   */
  const [typeSwitched, setTypeSwitched] = useState(false);
  /** `?type=praise` 고정 링크로 칭찬 릴레이 창구에 들어왔나(거래처가 창구를 켠 경우만 true). */
  const [praiseFixed, setPraiseFixed] = useState(false);
  /** `?type=quote` 고정 링크로 견적 창구에 들어왔나 + `&kind=` 로 정해진 구분. */
  const [quoteFixed, setQuoteFixed] = useState(false);
  const [quoteKind, setQuoteKind] = useState<QuoteKind | null>(null);

  useEffect(() => {
    const {
      tenantId: paramTenant,
      embedToken,
      directIntake: direct,
      entryType: entry,
      entryPraise,
      entryQuote,
      quoteKind: qk,
    } = readEmbedParams();
    setQuoteKind(qk);
    setDirectIntake(direct);
    setEntryType(entry);

    // `?f=` 로 들어오면 거래처를 키의 첫 조각에서 얻는다(브랜딩·«새로 문의하기» 용도).
    //   🔴 권한 판정은 여기서 하지 않는다 — 토큰 대조는 서버(openFollowUp)만 한다.
    //      클라가 판정하면 링크를 고쳐 남의 문의를 여는 길이 열린다.
    const fkey = readFollowUpKey();
    setFollowUpKey(fkey);
    const tenantId = paramTenant ?? (fkey ? fkey.split('~')[0] || null : null);
    if (!tenantId) {
      setLoad({ status: 'error' });
      return;
    }
    let cancelled = false;
    (async () => {
      const tenant = await loadTenant(tenantId, embedToken);
      if (cancelled) return;
      if (!tenant) {
        setLoad({ status: 'error' });
        return;
      }
      setLoad({ status: 'ok', tenant });
      // 칭찬 릴레이 고정 링크 — 창구를 끈 거래처면 평소 선택 화면으로 떨어진다(빈 화면 대신).
      if (entryPraise && tenant.praiseRelay === true) {
        setView('praise');
        setPraiseFixed(true);
        return;
      }
      // 견적 고정 링크 — 견적 창구가 아닌 거래처면 평소 선택 화면으로 떨어진다.
      if (entryQuote && QUOTE_TENANTS.includes(tenant.id)) {
        setView('quote');
        setQuoteFixed(true);
        return;
      }
      // 고정 유형 링크 — 선택 클릭과 동일한 진입(faqEnabled 는 tenant 로드 뒤에야 알 수 있어 여기서).
      if (entry) {
        setView(entry);
        setFromChatbot(direct);
        setShowIntake(skipChatbot(entry, tenant, direct));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // 거래처 brandColor 를 액센트로 주입(tokens.css 의 --accent-primary 계열 오버라이드).
  //   soft/softer 는 brandColor 에 알파를 얹어 hover 배경으로 사용.
  const brandStyle = useMemo(() => {
    if (load.status !== 'ok' || !load.tenant.brandColor) return undefined;
    const c = load.tenant.brandColor;
    return {
      '--accent-primary': c,
      '--accent-primary-hover': c,
      '--accent-primary-active': c,
      '--accent-primary-soft': `color-mix(in srgb, ${c} 16%, transparent)`,
      '--accent-primary-softer': `color-mix(in srgb, ${c} 8%, transparent)`,
    } as React.CSSProperties;
  }, [load]);

  if (load.status === 'loading') {
    return (
      <div className="min-h-dvh flex items-center justify-center text-text-tertiary text-sm">
        불러오는 중…
      </div>
    );
  }

  if (load.status === 'error') {
    return (
      <div className="min-h-dvh bg-base p-4 flex items-center justify-center">
        <Card className="w-full max-w-md text-center">
          <p className="text-sm text-text-secondary">
            고객센터를 불러올 수 없습니다.
          </p>
          <p className="text-xs text-text-tertiary mt-2">
            올바른 링크로 접속했는지 확인해 주세요. (거래처 정보 없음 또는 비활성)
          </p>
        </Card>
      </div>
    );
  }

  const tenant = load.tenant;
  // 제목이 따라야 할 유형 — 평소엔 고정 유형 링크(entryType), 고객이 옮겼으면 지금 쓰는 유형.
  const headerType: TicketType | null =
    typeSwitched && (view === 'inquiry' || view === 'lost_item') ? view : entryType;
  const praiseOn = tenant.praiseRelay === true;
  const quoteOn = QUOTE_TENANTS.includes(tenant.id);

  return (
    <div className="min-h-dvh bg-base p-4 flex justify-center" style={brandStyle}>
      <Card className="w-full max-w-md flex flex-col">
        <header className="mb-4">
          <div className="text-accent text-[11.5px] font-bold tracking-wider uppercase">
            {tenant.displayName}
          </div>
          <h1 className="text-lg font-bold tracking-tight">
            {praiseFixed
              ? '칭찬 릴레이 접수'
              : quoteFixed
                ? '전세버스 견적 요청'
                : headerType === 'inquiry'
                ? '고객 문의'
                : headerType === 'lost_item'
                  ? '분실물 접수'
                  : quoteOn
                    ? '문의 · 분실물 · 견적'
                    : '고객 문의 · 분실물 접수'}
          </h1>
        </header>

        {/* 「이어서 문의하기」 — 이 모드에선 유형 선택·챗봇·접수 폼을 통째로 건너뛴다.
            🔴 「새로 문의하기」는 키를 버리고 **평소 화면으로 되돌리는 것**이지 새 창을 여는 게 아니다
               (iframe 안에서 window.open 은 막히거나 부모 밖으로 튄다). */}
        {followUpKey ? (
          <FollowUpPanel fkey={followUpKey} onNewInquiry={() => setFollowUpKey(null)} />
        ) : view === 'select' ? (
          <div className="space-y-2">
            <p className="text-sm text-text-secondary mb-3">무엇을 도와드릴까요?</p>
            {(['inquiry', 'lost_item'] as TicketType[]).map((t) => (
              <button
                key={t}
                type="button"
                className="w-full text-left px-4 py-3 rounded-lg border border-border-default hover:border-accent hover:bg-accent-softer transition-colors"
                onClick={() => {
                  setView(t);
                  // 카카오 '접수하기'(?intake=1)는 이미 챗봇을 거쳐 넘어온 것 — 접수 출처로 태깅.
                  setFromChatbot(directIntake);
                  setShowIntake(skipChatbot(t, tenant, directIntake));
                }}
              >
                <span className="text-sm font-medium">{TICKET_TYPE_LABELS[t]}</span>
                <span className="block text-xs text-text-tertiary mt-0.5">
                  {t === 'inquiry'
                    ? '운행·시간·서비스 등 궁금한 점을 물어보세요.'
                    : '버스에 두고 내린 물건을 접수합니다.'}
                </span>
              </button>
            ))}
            {/* 견적 — 자사 홈페이지 창구(QUOTE_TENANTS)에만. */}
            {quoteOn && (
              <button
                type="button"
                className="w-full text-left px-4 py-3 rounded-lg border border-border-default hover:border-accent hover:bg-accent-softer transition-colors"
                onClick={() => setView('quote')}
              >
                <span className="text-sm font-medium">전세버스 견적</span>
                <span className="block text-xs text-text-tertiary mt-0.5">
                  통근·통학 정기 운행이나 관광·행사 전세 견적을 요청합니다.
                </span>
              </button>
            )}
            {/* 칭찬 릴레이 — 창구를 켠 거래처에만. 문의 폼과 칸이 달라(이름·연락처 없음) 따로 연다. */}
            {praiseOn && (
              <button
                type="button"
                className="w-full text-left px-4 py-3 rounded-lg border border-border-default hover:border-accent hover:bg-accent-softer transition-colors"
                onClick={() => setView('praise')}
              >
                <span className="text-sm font-medium">칭찬 릴레이</span>
                <span className="block text-xs text-text-tertiary mt-0.5">
                  친절한 기사님을 칭찬해 주세요.
                </span>
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {/* 고정 유형(?type=) 링크는 유형 선택 자체가 없다 — 뒤로 = 다른 창구 노출이라 감춘다. */}
            {!entryType && !praiseFixed && !quoteFixed && (
              <button
                type="button"
                className="text-xs text-text-tertiary hover:text-text-primary"
                onClick={() => {
                  setView('select');
                  setShowIntake(false);
                  setFromChatbot(false);
                }}
              >
                ← 뒤로
              </button>
            )}

            {/* 문의 + FAQ 활성 거래처: 챗봇 대화 먼저, 해결 안 되면 접수 폼 전환 */}
            {view === 'inquiry' && tenant.faqEnabled && !showIntake && (
              <ChatbotPanel
                tenantId={tenant.id}
                onEscalate={() => {
                  setFromChatbot(true);
                  setShowIntake(true);
                }}
                onHistoryChange={setChatHistory}
              />
            )}

            {view === 'praise' && (
              <div>
                {!praiseFixed && (
                  <div className="text-[10.5px] font-bold tracking-wider text-text-tertiary uppercase mb-2">
                    칭찬 릴레이 접수
                  </div>
                )}
                <PraiseForm tenantId={tenant.id} routes={tenant.routes ?? []} />
              </div>
            )}

            {view === 'quote' && <QuoteForm tenantId={tenant.id} initialKind={quoteKind ?? undefined} />}

            {showIntake && view !== 'praise' && view !== 'quote' && (
              <div>
                {/* 고정 유형 링크는 헤더가 이미 그 창구명이다 — 같은 말 두 번 안 적는다.
                    옮긴 뒤에는 헤더도 새 유형을 따르므로 마찬가지로 안 적는다. */}
                {!headerType && (
                  <div className="text-[10.5px] font-bold tracking-wider text-text-tertiary uppercase mb-2">
                    {TICKET_TYPE_LABELS[view]} 접수
                  </div>
                )}
                <IntakeForm
                  tenantId={tenant.id}
                  type={view}
                  routes={tenant.routes ?? []}
                  routeSchedule={tenant.routeSchedule ?? []}
                  routeScheduleAuto={tenant.routeScheduleAuto ?? []}
                  fromChatbot={fromChatbot}
                  chatHistory={chatHistory}
                  requirePhoneVerify={tenant.requirePhoneVerify === true}
                  askCompanyName={tenant.askCompanyName === true}
                  employeeNoLabel={tenant.employeeNoLabel ?? ''}
                  employeeNoRequired={tenant.employeeNoRequired === true}
                  sites={normalizeSites(tenant.sites)}
                  onSwitchToLost={
                    view === 'inquiry'
                      ? () => {
                          // 🔴 `key` 를 주지 않으므로 폼은 **같은 인스턴스**로 남고 입력값이 살아 있다.
                          //    유형만 바뀌어 분실물 칸(일자·시간대·물품)이 그 아래 붙는다.
                          setView('lost_item');
                          setTypeSwitched(true);
                        }
                      : undefined
                  }
                />
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
